/**
 * THE DOCUMENT, AS CONTROLS — one fieldset per block, in order.
 *
 * ★★ A LIST OF TYPED BLOCKS, AND INSIDE EACH TEXT OR QUOTE BLOCK THE RULED EDITOR. The document
 * stays the block union the contract carries (authors-api.yaml's `Block`) - its ids are what the
 * review anchors point at, so blocks are added, moved and removed here as blocks. The TEXT of a text
 * or quote block is edited in Tiptap (ADR-0008, decision-log row 33: "today's four +
 * headings/lists/links"), through `@moonx/ui`'s `EditorShell`, by ./TextBlockEditor.tsx - which
 * turns the editor's document into `body`, paragraph rows and marks with
 * `shared/content-model/editor-json.ts`, the converter `adr/0008:42` puts in that package. The
 * editor's HTML never reaches the API (authors-api.yaml's `TextBlock`).
 *
 * ★ WHAT THE TOOLBAR OFFERS IS EXACTLY WHAT THE PLATFORM STORES: paragraph, heading 2-4, bulleted
 * and numbered lists (three levels of nesting below the first) on a text block; bold, italic,
 * underline, strikethrough, code and links (`https://`, `http://`, `mailto:`) on both kinds. A
 * quotation has no paragraph styles - `QuoteBlock` declares no `paragraphs` - and no heading or
 * list control is drawn over one (register R-002, lens finding C2-02).
 *
 * ★ THE ROWS ARE RECOMPUTED ON EVERY CHANGE, NEVER CARRIED. This file used to spread the server's
 * block and put the new body on it, `{ ...found, body }` - so a reopened draft sent the old
 * paragraph rows beside a new text and every save was refused `PARAGRAPH_ROWS_DISAGREE` (register
 * R-001, lens finding C2-01). Each change now replaces `body`, `paragraphs` and `marks` together
 * with what the converter derived from the new text.
 *
 * ★ "ADD AN IMAGE" IS AN UPLOAD, NOT A FOURTH `newBlock` KIND. An image block requires a `fileId`
 * the platform mints through `claimArticleImageUpload`, so the file is written and claimed first
 * (./ImageUploadButton.tsx) and the block is appended only once the claim has answered, with its
 * alt text empty for the author to write (./document.ts `newBlock` still refuses the kind).
 *
 * ★ EVERY PICTURE'S `src` GOES THROUGH `emulatorImageUrl`. The API's addresses are production
 * Storage's, which nothing serves on a laptop; against the local stack the same object is read
 * from the emulator, and in every other build the address is left exactly as it came
 * (features/storage/emulator-image-url.ts).
 */
import { ActionIcon, Button, Group, Stack, Text, TextInput, Tooltip } from '@mantine/core';
import { countTextLength } from '../../../features/cabinet/api/editor-measure';
import { emulatorImageUrl, servedUrlOnError } from '../../../features/storage/emulator-image-url';
import { useTranslation } from '../../../i18n';
import type { ArticlesT } from '../articles-i18n';
import { ImageUploadButton } from './ImageUploadButton';
import { TextBlockEditor } from './TextBlockEditor';
import { imageBlock, moveBlock, newBlock, newBlockId, removeBlock, replaceBlock } from './document';
import type { Block } from '../../../features/cabinet/api/editor-api';
import type { EditorDraft } from './document';
import { usePieceContext } from './piece-context';

export interface BlockListProps {
  /** The article a picture is claimed for. */
  readonly articleId: string;
  readonly blocks: readonly Block[];
  readonly disabled: boolean;
  /** The server's own `updatedAt`. See ./document.ts: this app mints no instants of its own. */
  readonly serverInstant: string;
  readonly onChange: (change: (draft: EditorDraft) => EditorDraft) => void;
  /** The bound `quote_length` sets on a quote's own text, when the policy sent one. */
  readonly quoteBodyMax: number | null;
  /** The bound it sets on the attribution line. Added 2026-09-20; until then no client could see it. */
  readonly quoteAttributionMax: number | null;
  /** False for a video's description (v1.0.29): text only, so no "Add an image" is offered. */
  readonly allowImages?: boolean;
}

function label(block: Block, index: number, t: ArticlesT): string {
  const number = index + 1;
  switch (block.kind) {
    case 'text':
      return t('editor.blocks.label.text', { number });
    case 'quote':
      return t('editor.blocks.label.quote', { number });
    case 'image':
      return t('editor.blocks.label.image', { number });
    case 'divider':
      return t('editor.blocks.label.divider', { number });
    default:
      return t('editor.blocks.label.other', { number });
  }
}

export function BlockList({
  articleId,
  blocks,
  disabled,
  serverInstant,
  onChange,
  quoteBodyMax,
  quoteAttributionMax,
  allowImages = true,
}: BlockListProps) {
  const { t } = useTranslation('articles');
  const context = usePieceContext();
  const setBlocks = (next: (current: readonly Block[]) => readonly Block[]) => {
    onChange((draft) => ({ ...draft, blocks: next(draft.blocks) }));
  };

  return (
    <Stack gap="md">
      <Stack gap="lg" role="group" aria-label={t('editor.blocks.listLabel', { context })}>
        {blocks.map((block, index) => (
          <Stack
            key={block.blockId}
            component="section"
            gap="xs"
            aria-label={label(block, index, t)}
            style={{
              borderInlineStart: '3px solid oklch(var(--mx-border))',
              paddingInlineStart: 'var(--mantine-spacing-sm)',
            }}
          >
            <Group justify="space-between" wrap="nowrap">
              <Text size="xs" c="dimmed" fw={700} tt="uppercase">
                {label(block, index, t)}
              </Text>
              <Group gap={4}>
                {/* THE ORDER IS PART OF THE DOCUMENT. A pure reorder is a real change and is saved
                    (WC-CR-07); ./document.ts `moveBlock` says why at length. */}
                <Tooltip label={t('editor.blocks.moveUp')} withArrow>
                  <ActionIcon
                    variant="subtle"
                    size="sm"
                    aria-label={t('editor.blocks.moveBlockUp', { block: label(block, index, t) })}
                    disabled={disabled || index === 0}
                    onClick={() => {
                      setBlocks((current) => moveBlock(current, block.blockId, -1));
                    }}
                  >
                    ↑
                  </ActionIcon>
                </Tooltip>
                <Tooltip label={t('editor.blocks.moveDown')} withArrow>
                  <ActionIcon
                    variant="subtle"
                    size="sm"
                    aria-label={t('editor.blocks.moveBlockDown', { block: label(block, index, t) })}
                    disabled={disabled || index === blocks.length - 1}
                    onClick={() => {
                      setBlocks((current) => moveBlock(current, block.blockId, 1));
                    }}
                  >
                    ↓
                  </ActionIcon>
                </Tooltip>
                <Tooltip label={t('editor.blocks.remove')} withArrow>
                  <ActionIcon
                    variant="subtle"
                    size="sm"
                    aria-label={t('editor.blocks.removeBlock', { block: label(block, index, t) })}
                    disabled={disabled}
                    onClick={() => {
                      setBlocks((current) => removeBlock(current, block.blockId));
                    }}
                  >
                    ×
                  </ActionIcon>
                </Tooltip>
              </Group>
            </Group>

            {block.kind === 'text' ? (
              <Stack gap={4}>
                <Text size="sm" c="dimmed">
                  {t('editor.blocks.textHint')}
                </Text>
                <TextBlockEditor
                  block={block}
                  label={label(block, index, t)}
                  toolbarLabel={t('editor.blocks.toolbar.text', { number: index + 1 })}
                  disabled={disabled}
                  onChange={(text) => {
                    setBlocks((current) =>
                      replaceBlock(current, block.blockId, (found) =>
                        found.kind === 'text'
                          ? {
                              ...found,
                              body: text.body,
                              paragraphs: text.paragraphs,
                              marks: text.marks,
                            }
                          : found,
                      ),
                    );
                  }}
                />
              </Stack>
            ) : null}

            {block.kind === 'quote' ? (
              <Stack gap="xs">
                <TextBlockEditor
                  block={block}
                  label={label(block, index, t)}
                  toolbarLabel={t('editor.blocks.toolbar.quote', { number: index + 1 })}
                  disabled={disabled}
                  error={
                    quoteBodyMax !== null && countTextLength(block.body) > quoteBodyMax
                      ? t('common.ofCharacters', {
                          count: quoteBodyMax,
                          value: String(countTextLength(block.body)),
                        })
                      : null
                  }
                  onChange={(text) => {
                    // A quote declares no `paragraphs` (strict on the wire): body and marks only.
                    setBlocks((current) =>
                      replaceBlock(current, block.blockId, (found) =>
                        found.kind === 'quote'
                          ? { ...found, body: text.body, marks: text.marks }
                          : found,
                      ),
                    );
                  }}
                />
                <TextInput
                  label={t('editor.blocks.attribution')}
                  // IT COUNTS TOWARD THE WORD COUNT, which the legacy editor's number did not —
                  // one of the reasons the two numbers differ (authors-api.yaml's QuoteBlock).
                  description={t('editor.blocks.attributionNote', { context })}
                  disabled={disabled}
                  value={block.quoteAttribution ?? ''}
                  error={
                    quoteAttributionMax !== null &&
                    countTextLength(block.quoteAttribution ?? '') > quoteAttributionMax
                      ? t('common.ofCharacters', {
                          count: quoteAttributionMax,
                          value: String(countTextLength(block.quoteAttribution ?? '')),
                        })
                      : null
                  }
                  onChange={(event) => {
                    const value = event.currentTarget.value;
                    const quoteAttribution = value.length === 0 ? null : value;
                    setBlocks((current) =>
                      replaceBlock(current, block.blockId, (found) =>
                        found.kind === 'quote' ? { ...found, quoteAttribution } : found,
                      ),
                    );
                  }}
                />
              </Stack>
            ) : null}

            {block.kind === 'image' ? (
              <Stack gap="xs">
                {block.url === undefined ? (
                  /* THE READ SIDE OMITS `url` ON A ROUND-TRIPPED IMAGE BLOCK — an open contract
                     question filed on 2026-09-20 (test/acceptance/draft-a4.spec.ts, the step-6
                     [todo-owner]). The block still holds its media id, so this says what it has
                     rather than rendering a broken picture. */
                  <Text size="sm" c="dimmed">
                    {/* Design 15: this named the media id. No id goes on screen. */}
                    {t('editor.blocks.imageNotShown')}
                  </Text>
                ) : (
                  <img
                    src={emulatorImageUrl(block.url)}
                    onError={servedUrlOnError(block.url)}
                    alt={block.imageAlt ?? ''}
                    style={{ maxWidth: '100%', borderRadius: 'var(--mantine-radius-sm)' }}
                  />
                )}
                <TextInput
                  label={t('editor.blocks.altText')}
                  description={t('editor.blocks.altTextNote')}
                  disabled={disabled}
                  value={block.imageAlt ?? ''}
                  onChange={(event) => {
                    const value = event.currentTarget.value;
                    const imageAlt = value.length === 0 ? null : value;
                    setBlocks((current) =>
                      replaceBlock(current, block.blockId, (found) =>
                        found.kind === 'image' ? { ...found, imageAlt } : found,
                      ),
                    );
                  }}
                />
                <TextInput
                  label={t('editor.blocks.caption')}
                  description={t('editor.blocks.captionNote')}
                  disabled={disabled}
                  value={block.caption ?? ''}
                  onChange={(event) => {
                    const value = event.currentTarget.value;
                    const caption = value.length === 0 ? null : value;
                    setBlocks((current) =>
                      replaceBlock(current, block.blockId, (found) =>
                        found.kind === 'image' ? { ...found, caption } : found,
                      ),
                    );
                  }}
                />
              </Stack>
            ) : null}

            {block.kind === 'divider' ? (
              <Text size="sm" c="dimmed">
                {t('editor.blocks.dividerNote', { context })}
              </Text>
            ) : null}
          </Stack>
        ))}
      </Stack>

      <Group gap="xs" align="flex-start" role="group" aria-label={t('editor.blocks.addBlock')}>
        <Button
          variant="default"
          size="compact-sm"
          disabled={disabled}
          onClick={() => {
            setBlocks((current) => [...current, newBlock('text', serverInstant)]);
          }}
        >
          {t('editor.blocks.addText')}
        </Button>
        <Button
          variant="default"
          size="compact-sm"
          disabled={disabled}
          onClick={() => {
            setBlocks((current) => [...current, newBlock('quote', serverInstant)]);
          }}
        >
          {t('editor.blocks.addQuote')}
        </Button>
        <Button
          variant="default"
          size="compact-sm"
          disabled={disabled}
          onClick={() => {
            setBlocks((current) => [...current, newBlock('divider', serverInstant)]);
          }}
        >
          {t('editor.blocks.addDivider')}
        </Button>
        {allowImages ? (
          <ImageUploadButton
            articleId={articleId}
            role="body"
            label={t('editor.blocks.addImage')}
            doneMessage={t('editor.blocks.imageAdded')}
            disabled={disabled}
            onClaimed={(image) => {
              // The id from the SERVER's instant, as every block's is (./document.ts `newBlockId`),
              // and the alt text left empty: only the author knows what the picture shows.
              const block = imageBlock(newBlockId(serverInstant), image.fileId, image.url, null);
              setBlocks((current) => [...current, block]);
            }}
          />
        ) : null}
      </Group>
    </Stack>
  );
}
